import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  Building2,
  CalendarDays,
  Clock3,
  Footprints,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from "lucide-react";

function getTodayThai() {
  return new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Bangkok" });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("th-TH");
}

function formatThaiDate(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${date}T00:00:00`));
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/95 px-4 py-3 text-white shadow-xl backdrop-blur">
      <p className="text-xs text-slate-300">เวลา {label} น.</p>
      <p className="mt-1 text-xl font-semibold">{formatNumber(payload[0].value)} <span className="text-xs font-normal text-slate-300">คน</span></p>
    </div>
  );
}

function StoreTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{formatNumber(payload[0].value)} <span className="text-xs font-normal text-slate-400">คน</span></p>
    </div>
  );
}

function MetricCard({ title, value, suffix, icon: Icon, tone = "teal", detail }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700 ring-teal-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
  };

  return (
    <div className="group rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="mt-2 flex items-end gap-1.5">
            <span className="text-3xl font-semibold tracking-tight text-slate-900">{value}</span>
            {suffix && <span className="pb-1 text-xs text-slate-400">{suffix}</span>}
          </div>
          {detail && <p className="mt-2 truncate text-xs text-slate-400">{detail}</p>}
        </div>
        <div className={`rounded-xl p-2.5 ring-1 ${tones[tone]}`}><Icon size={20} strokeWidth={1.8} /></div>
      </div>
    </div>
  );
}

export default function PassByDashboard() {
  const [date, setDate] = useState(getTodayThai());
  const [summary, setSummary] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [byStore, setByStore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async ({ silent = false } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    setError("");
    const token = localStorage.getItem("token");

    try {
      const responses = await Promise.all([
        fetch(`/api/dashboard/passby/summary?date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/dashboard/passby/hourly?date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/dashboard/passby/by-store?date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (responses.some((response) => !response.ok)) throw new Error("ไม่สามารถโหลดข้อมูล Pass-by ได้");
      const [summaryRes, hourlyRes, storeRes] = await Promise.all(responses.map((response) => response.json()));

      setSummary(summaryRes || {});
      setHourly(Array.isArray(hourlyRes) ? hourlyRes : []);
      setByStore(Array.isArray(storeRes) ? storeRes : []);
    } catch (fetchError) {
      setError(fetchError.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const insights = useMemo(() => {
    const activeHours = hourly.filter((item) => Number(item.count) > 0);
    const peak = hourly.reduce((max, item) => Number(item.count) > Number(max?.count || 0) ? item : max, null);
    const average = activeHours.length
      ? Math.round(activeHours.reduce((sum, item) => sum + Number(item.count || 0), 0) / activeHours.length)
      : 0;
    const sortedStores = [...byStore].sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
    return { peak, average, sortedStores, maxStore: Number(sortedStores[0]?.total || 0) };
  }, [hourly, byStore]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-5 md:p-8">
        <div className="mx-auto max-w-[1600px] animate-pulse space-y-6">
          <div className="h-20 rounded-2xl bg-slate-200" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[1, 2, 3, 4].map((item) => <div key={item} className="h-32 rounded-2xl bg-white" />)}</div>
          <div className="h-96 rounded-2xl bg-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-teal-700"><span className="h-2 w-2 rounded-full bg-teal-500" /> Foot traffic analytics</div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">ภาพรวมคนเดินผ่านหน้าร้าน</h1>
            <p className="mt-1 text-sm text-slate-500">วิเคราะห์ความหนาแน่นรายชั่วโมงและเปรียบเทียบทราฟฟิกของแต่ละสาขา</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-sm focus-within:border-teal-500 focus-within:ring-4 focus-within:ring-teal-500/10">
              <CalendarDays size={17} className="text-slate-400" />
              <input type="date" value={date} max={getTodayThai()} onChange={(event) => setDate(event.target.value)} className="bg-transparent text-sm text-slate-700 outline-none" />
            </div>
            <button onClick={() => fetchData({ silent: true })} disabled={refreshing} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-60">
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "กำลังอัปเดต" : "อัปเดตข้อมูล"}
            </button>
          </div>
        </header>

        <div className="flex items-center gap-2 rounded-xl border border-teal-100 bg-teal-50/70 px-4 py-3 text-sm text-teal-800">
          <Activity size={16} /> ข้อมูลประจำวันที่ <span className="font-semibold">{formatThaiDate(date)}</span>
        </div>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <MetricCard title="คนเดินผ่านทั้งหมด" value={formatNumber(summary?.total_passby)} suffix="คน / วัน" icon={Footprints} detail="ยอดรวมจากทุกสาขา" />
          <MetricCard title="ช่วงเวลาสูงสุด" value={insights.peak?.hour || "—"} suffix={insights.peak ? "น." : ""} icon={Clock3} tone="amber" detail={insights.peak ? `${formatNumber(insights.peak.count)} คนในช่วงเวลานี้` : "ยังไม่มีข้อมูล"} />
          <MetricCard title="เฉลี่ยต่อชั่วโมง" value={formatNumber(insights.average)} suffix="คน" icon={TrendingUp} tone="blue" detail="คำนวณจากช่วงเวลาที่มีข้อมูล" />
          <MetricCard title="สาขาที่มีข้อมูล" value={formatNumber(byStore.length)} suffix="สาขา" icon={Building2} tone="teal" detail={insights.sortedStores[0]?.device_name ? `สูงสุด: ${insights.sortedStores[0].device_name}` : "ยังไม่มีข้อมูล"} />
        </section>

        <section className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:p-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-teal-100/70 blur-3xl" />
          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div><h2 className="font-semibold text-slate-900">แนวโน้มคนเดินผ่านรายชั่วโมง</h2><p className="mt-1 text-xs text-slate-500">ดูช่วงเวลาที่มีคนเดินผ่านหน้าร้านหนาแน่นที่สุด</p></div>
            {insights.peak && <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 ring-1 ring-amber-100"><Sparkles size={15} /> Peak {insights.peak.hour} น. · {formatNumber(insights.peak.count)} คน</div>}
          </div>
          <div className="relative mt-5 h-[330px] min-h-[330px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hourly} margin={{ top: 12, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="passbyArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00b3b4" stopOpacity={0.35} /><stop offset="100%" stopColor="#00b3b4" stopOpacity={0.02} /></linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="4 4" />
                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} dy={10} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip content={<TrendTooltip />} cursor={{ stroke: "#99f6e4", strokeWidth: 1 }} />
                <Area type="monotone" dataKey="count" stroke="#008f90" strokeWidth={3} fill="url(#passbyArea)" activeDot={{ r: 6, fill: "#008f90", stroke: "#fff", strokeWidth: 3 }} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:p-6">
            <div><h2 className="font-semibold text-slate-900">เปรียบเทียบแต่ละสาขา</h2><p className="mt-1 text-xs text-slate-500">จำนวนคนเดินผ่านรวม แยกตามสาขา</p></div>
            <div className="mt-5 h-[330px] min-h-[330px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={insights.sortedStores} margin={{ top: 8, right: 8, left: -18, bottom: 14 }} barCategoryGap="30%">
                  <defs><linearGradient id="storeBar" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#38bdf8" /><stop offset="100%" stopColor="#0ea5e9" /></linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="4 4" />
                  <XAxis dataKey="device_name" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} dy={10} interval={0} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip content={<StoreTooltip />} cursor={{ fill: "#f0f9ff" }} />
                  <Bar dataKey="total" fill="url(#storeBar)" radius={[9, 9, 4, 4]} maxBarSize={62} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div><h2 className="font-semibold text-slate-900">อันดับสาขา</h2><p className="mt-0.5 text-xs text-slate-500">เรียงตามจำนวนคนเดินผ่าน</p></div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{byStore.length} สาขา</span>
            </div>
            <div className="divide-y divide-slate-100">
              {insights.sortedStores.map((row, index) => {
                const percent = insights.maxStore ? Math.round((Number(row.total || 0) / insights.maxStore) * 100) : 0;
                return (
                  <div key={row.store_id} className="px-5 py-4 transition hover:bg-slate-50/80">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${index === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{index + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3"><span className="truncate text-sm font-medium text-slate-700">{row.device_name || `Store ${row.store_id}`}</span><span className="whitespace-nowrap text-sm font-semibold text-slate-900">{formatNumber(row.total)} <small className="font-normal text-slate-400">คน</small></span></div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-teal-400" style={{ width: `${percent}%` }} /></div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!byStore.length && <div className="px-5 py-16 text-center text-sm text-slate-400">ไม่พบข้อมูลสาขาในวันที่เลือก</div>}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
